import { Fragment, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  Loading,
  ModalBody,
  ModalHeader,
  Tab,
  TabList,
  Tabs,
  Tag,
  Tile,
} from "@carbon/react";
import { SAMPLE_STATE } from "../../utils";
import { Chat, MaterialOffer, Slide } from "..";
import PageHeader from "../carbon/PageHeader/PageHeader";
import Detail from "./Detail/Detail";
import Delegate from "./Delegate/Delegate";
import { useID } from "../../hooks";
import Suppliers from "./Suppliers/Suppliers";
import BiddingList from "./BiddingList/BiddingList";
import Actions from "./Actions/Actions";
import InviteSupplier from "./InviteSupplier/InviteSupplier";
import PotencialSupplier from "./PotencialSupplier/PotencialSupplier";
import {
  CombinedBuyerSellerMaterialOfferResponse,
  MaterialResponse,
} from "../../types";
import { useCountriesByMaterialId } from "../../hooks/useCountries";
import { useChat, useCreateChat, useCreateMessage } from "../../hooks/useChat";
import {
  useToggleMaterialStatus,
  useUpdateMaterial,
} from "../../hooks/useMaterials";
import {
  useRejectSeller,
  useValidateSeller,
} from "../../hooks/useValidateSeller";
import {
  useCreateMaterialOfferInvitation,
  useMaterialOffers,
  useUpdateMaterialOffer,
  useRequestSample,
} from "../../hooks/useMaterialsOffers";
import { queryKeys } from "../../hooks/queryKeys";
import { useNotification, useUser } from "../../context";
import "./material.scss";

type MaterialProps = {
  material: MaterialResponse;
};

/**
 * The material DETAIL screen's orchestrator — rebuilt on the Carbon primitives (Task 8) for its
 * OWN chrome: the back affordance, `PageHeader`, the Detail/RFQs tab switcher, and the
 * Detail/Delegate/Actions/tag cluster. `Suppliers`, `InviteSupplier`, `PotencialSupplier`,
 * `MaterialOffer`, `BiddingList` and `Chat` are rendered exactly as before, with exactly the same
 * props — per the task's scope fence, their own internals (all Orders/Offers/suppliers-owned) are
 * untouched, only carried over mechanically.
 *
 * Tabs: Carbon `Tabs`/`TabList`/`Tab` drive `selectedTab`, the same pattern
 * `containers/Tags/Tags.tsx` already uses (a bare `TabList` for the switcher, the tab body
 * rendered manually below rather than through `TabPanels`) — replaces the pre-Carbon
 * `components/UI/Tabs` (Tailwind), which stays in place for its other, unmigrated callers.
 *
 * The Delegate dialog: `Delegate` now manages its own Carbon `ConfirmDialog` when handed
 * `open={openDelegate}` (see `Delegate.tsx`'s own docstring) — no more wrapping `<Slide>` for
 * this one dialog. Every other `Slide`-wrapped panel here (MaterialOffer, InviteSupplier,
 * PotencialSupplier, Chat) is untouched, out of this task's scope.
 */
const Material = ({ material }: MaterialProps) => {
  const { openNotification } = useNotification();
  const { user } = useUser();
  const { t } = useTranslation();

  const id = useID();
  const [selectedTab, setSelectedTab] = useState(0);
  const userId = new URLSearchParams(useLocation().search).get("userId");
  const navigate = useNavigate();
  const goToBidding = () =>
    navigate({
      pathname: "/orders/new",
      search: `?id=${id}`,
    });

  const [open, setOpen] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const [openDelegate, setOpenDelegate] = useState(false);
  const [openSupplier, setOpenSupplier] = useState(false);
  const [potencialSupplierToShow, setPotencialSupplierToShow] =
    useState<any>("");
  const [materialOfferToShow, setMaterialOfferToShow] =
    useState<CombinedBuyerSellerMaterialOfferResponse>();

  const { data: materialOffers, isLoading: isLoadingMaterialOffer } =
    useMaterialOffers(id, userId || "");

  const { data: countriesByMaterial } = useCountriesByMaterialId(
    id,
    material.category.type
  );

  const { data: chat, isLoading: isLoadingChat } = useChat(
    materialOfferToShow?.materialOffer?.user?.id || "",
    material?.id,
    openChat
  );

  const { mutate: mutateChat, isPending: isLoadingCreateChat } = useCreateChat([
    queryKeys.CHAT,
  ]);

  const { mutate: mutateMessage, isPending: isLoadingMessage } =
    useCreateMessage([queryKeys.CHAT]);

  const { mutate: mutateMaterial, isPending: isLoadingMaterials } =
    useUpdateMaterial(() => {
      openNotification(t("material.retrieveMessage"));
    });

  const { mutate: rejectSeller, isPending: isRejectingSeller } =
    useRejectSeller(
      () => openNotification(t("offer.rejectedApplication")),
      () => openNotification(t("offer.error"))
    );

  const { mutate: validateSeller, isPending: isValidating } =
    useValidateSeller();

  const { mutate: qualifySample, isPending: isQualifyingSample } =
    useUpdateMaterialOffer((res) => {
      openNotification(
        res.state === SAMPLE_STATE.REJECTED
          ? t("offer.rejected")
          : t("offer.approved")
      );
      return setOpen(false);
    });

  const { mutate: requestSample, isPending: isRequestingSample } =
    useRequestSample(
      () => {
        setOpen(false);
        return openNotification(t("offer.requestedSample"));
      },
      () => openNotification(t("offer.error"))
    );

  const { mutate: toggleMaterialStatus, isPending: isTogglingMaterialStatus } =
    useToggleMaterialStatus();

  const {
    mutate: createMaterialOfferInvitation,
    isPending: isCreatingInvitation,
  } = useCreateMaterialOfferInvitation(
    () => openNotification(t("invite.successSupplierInvite")),
    () => openNotification(t("newMaterialForm.error"))
  );

  const handleInviteSupplier = () => setOpenInvite(true);
  const onCloseInvitation = () => setOpenInvite(false);

  const handleDelegate = () => setOpenDelegate(true);
  const onCloseDelegate = () => setOpenDelegate(false);

  const handleRegain = () => {
    mutateMaterial({
      materialId: material?.id,
      body: {
        ...material,
        user,
        owner: { id: user?.id },
      },
    });
  };

  if (
    isLoadingMaterials ||
    isRejectingSeller ||
    isQualifyingSample ||
    isRequestingSample ||
    isCreatingInvitation ||
    isTogglingMaterialStatus
  )
    return (
      <div className="material-detail-page__loading">
        <Loading withOverlay={false} description={t("cta.loading")} />
      </div>
    );

  const pnType = material?.category?.name;

  const isOwner = user?.id === material?.user?.id;
  const isResponsible = user?.id === material?.owner?.id;

  return (
    <Fragment>
      <Button
        kind="ghost"
        size="sm"
        className="material-detail-page__back"
        onClick={() => navigate(-1)}
      >
        {t("cta.goBack")}
      </Button>

      <Tile className="material-detail-page__header">
        <PageHeader
          title={
            <>
              {material?.globalMaterialName}
              <span className="material-detail-page__ean">{material?.ean}</span>
              <Tag
                type={material?.active ? "green" : "gray"}
                size="sm"
                className="material-detail-page__status"
              >
                {material?.active ? t("cta.active") : t("cta.inactive")}
              </Tag>
            </>
          }
          action={
            <Actions
              isOwner={isOwner}
              isResponsible={isResponsible}
              material={material}
              goToBidding={goToBidding}
              onClick={handleDelegate}
              onClickRegain={handleRegain}
              onToggleMaterialStatus={toggleMaterialStatus}
            />
          }
        />
      </Tile>

      <div className="material-detail-page__tabs">
        <Tabs
          selectedIndex={selectedTab}
          onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
        >
          <TabList aria-label={t("material.detail")}>
            <Tab>{t("material.detail")}</Tab>
            <Tab>{t("material.tenders")}</Tab>
          </TabList>
        </Tabs>
      </div>

      {selectedTab === 0 && (
        <div className="material-detail-page__grid">
          <div className="material-detail-page__column">
            <Tile className="material-detail-page__card">
              <Detail material={material} pnType={pnType} />
            </Tile>
          </div>
          {isValidating ? (
            <Loading withOverlay={false} description={t("cta.loading")} />
          ) : (
            <Suppliers
              materialId={id}
              countryId={
                material?.deliveryPlace?.country?.id ??
                material?.company?.country?.id ??
                ""
              }
              isOwner={material?.user?.id === material?.owner?.id}
              setOpen={setOpen}
              isActive={material?.active}
              onClick={handleInviteSupplier}
              materialOffers={materialOffers}
              setOpenSupplier={setOpenSupplier}
              setOpenChat={setOpenChat}
              countriesByMaterial={countriesByMaterial}
              setMaterialOfferToShow={setMaterialOfferToShow}
              setPotencialSupplierToShow={setPotencialSupplierToShow}
            />
          )}
        </div>
      )}
      {selectedTab === 1 && <BiddingList materialId={material?.id} />}
      {materialOfferToShow && (
        <MaterialOffer
          open={open}
          setOpen={() => setOpen(false)}
          onRejectSeller={rejectSeller}
          onApproveSample={qualifySample}
          onRejectSample={qualifySample}
          onRequestSample={requestSample}
          materialOffer={materialOfferToShow}
          onValidateSeller={validateSeller}
          onChangeToggle={() => setOpen(false)}
        />
      )}
      <ComposedModal
        open={openInvite}
        onClose={onCloseInvitation}
        size="sm"
      >
        <ModalHeader title={t("invite.invite")} />
        <ModalBody hasForm>
          <InviteSupplier
            materialId={id}
            onCloseInvitation={onCloseInvitation}
          />
        </ModalBody>
      </ComposedModal>
      <Delegate
        material={material}
        open={openDelegate}
        onCloseDelegate={onCloseDelegate}
      />
      {potencialSupplierToShow && (
        <PotencialSupplier
          open={openSupplier}
          materialId={id}
          isLoadingMaterialOffer={isLoadingMaterialOffer}
          onCreateMaterialOfferInvitation={createMaterialOfferInvitation}
          potencialSupplier={potencialSupplierToShow}
          setOpenSupplier={setOpenSupplier}
        />
      )}
      <Slide open={openChat} setOpen={setOpenChat}>
        <Chat
          chat={chat}
          seller={materialOfferToShow}
          material={material}
          onCreateChat={mutateChat}
          onCreateMessage={mutateMessage}
          isLoadingChat={
            isLoadingChat || isLoadingMessage || isLoadingCreateChat
          }
        />
      </Slide>
    </Fragment>
  );
};

export default Material;
